import {
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	If,
	makeClassName,
	Row,
	toLocalDateString,
	Tooltip,
	TooltipContainerProps,
	TooltipProps,
	TruncatedText,
} from "@hex-insights/core";
import {
	CourseSectionNameFragment,
	Employee,
	EmployeeNameAndImageFragment,
	HomeRoomSectionNameFragment,
	TeacherCourseSectionEnrollment,
	TeacherHomeRoomSectionEnrollment,
	useEmployeeTooltipLazyQuery,
	useLazyTooltip,
} from "../../../../Utilities";
import { CourseSectionFormat, EmployeeFormat, HomeRoomSectionFormat } from "../../../Format";
import { EmployeeIconNameDescription } from "../General";
import styles from "./styles.module.css";

export type TooltipEmployee = Pick<Employee, "id" | "jobTitle"> &
	EmployeeNameAndImageFragment & {
		teacherHomeRoomSectionEnrollments: (Pick<TeacherHomeRoomSectionEnrollment, "id"> & {
			homeRoomSection: HomeRoomSectionNameFragment;
		})[];
		teacherCourseSectionEnrollments: (Pick<TeacherCourseSectionEnrollment, "id"> & {
			courseSection: CourseSectionNameFragment;
		})[];
	};

export type EmployeeTooltipProps = {
	isLoading?: boolean;
	employee: TooltipEmployee | null | undefined;
} & Omit<TooltipProps, "children">;

export function EmployeeTooltip({ isLoading = false, employee, className, ...props }: EmployeeTooltipProps) {
	return (
		<Tooltip className={makeClassName(styles["tooltip"], "tooltip--big", className)} {...props}>
			<Tooltip.Body>
				<Conditional>
					<If condition={isLoading && !employee}>Loading</If>
					<Else>{!!employee && <EmployeeTooltipContent employee={employee} />}</Else>
				</Conditional>
			</Tooltip.Body>
		</Tooltip>
	);
}

type EmployeeTooltipContentProps = {
	employee: TooltipEmployee;
};

function EmployeeTooltipContent({ employee }: EmployeeTooltipContentProps) {
	return (
		<Column justify="spaced-start">
			<EmployeeIconNameDescription
				employee={employee}
				noLink
				iconSize={40}
				description={<TruncatedText>{EmployeeFormat.Fields.jobTitle(employee.jobTitle)}</TruncatedText>}
			/>

			<Column justify="spaced-start" verticalSpacing="0.125rem">
				<Conditional>
					<If condition={employee.teacherHomeRoomSectionEnrollments.length > 0}>
						<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
							<span className={styles["tooltip__label"]}>Home Room</span>
							<TruncatedText className={styles["tooltip__value"]}>
								{englishCommaJoinStrings(
									employee.teacherHomeRoomSectionEnrollments.map((e) => HomeRoomSectionFormat.name(e.homeRoomSection)),
								)}
							</TruncatedText>
						</Row>
					</If>
					<If condition={employee.teacherCourseSectionEnrollments.length > 0}>
						<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
							<span className={styles["tooltip__label"]}>Home Room</span>
							<TruncatedText className={styles["tooltip__value"]}>
								{englishCommaJoinStrings(
									employee.teacherCourseSectionEnrollments.map((e) => CourseSectionFormat.name(e.courseSection)),
								)}
							</TruncatedText>
						</Row>
					</If>
				</Conditional>
			</Column>
		</Column>
	);
}

export type EmployeeTooltipContainerProps = Omit<TooltipContainerProps, "onOpenChange" | "contentSyncID"> & {
	employeeID: Employee["id"];
};

EmployeeTooltip.Container = function ({ employeeID, children, ...props }: EmployeeTooltipContainerProps) {
	const [load, { loading, data }] = useEmployeeTooltipLazyQuery({
		variables: { id: employeeID, date: toLocalDateString(new Date()) },
	});
	const { onOpenChange, contentSyncID, className: tooltipClassName } = useLazyTooltip(load, !!data);

	return (
		<Tooltip.Container onOpenChange={onOpenChange} contentSyncID={contentSyncID} {...props}>
			<EmployeeTooltip isLoading={loading} employee={data?.employee} className={tooltipClassName} />

			{children}
		</Tooltip.Container>
	);
};
